import { useState, useEffect, useCallback } from "react";

const VIEW_KEY = "erp_list_view_";
const COLUMNS_KEY = "erp_list_columns_";

/**
 * Persist list view mode (table | cards) per listId in localStorage.
 * @param {string} listId - Unique id for the list page (e.g. 'enquiries', 'users')
 * @param {string} defaultView - 'table' | 'cards'
 */
export function useListViewMode(listId, defaultView = "table") {
  const key = VIEW_KEY + listId;
  const [viewMode, setViewModeState] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === "table" || stored === "cards") return stored;
    } catch (_) {}
    return defaultView;
  });

  const setViewMode = useCallback(
    (next) => {
      setViewModeState((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        try {
          localStorage.setItem(key, value);
        } catch (_) {}
        return value;
      });
    },
    [key]
  );

  return [viewMode, setViewMode];
}

/**
 * Column config: { id, label, minWidth?, defaultWidth?, sortable?, visible? }
 * Persisted: { order: string[], visibility: Record<id, boolean>, widths: Record<id, number> }
 */
export function useColumnPreferences(listId, defaultColumns) {
  const key = COLUMNS_KEY + listId;
  const defaultOrder = defaultColumns.map((c) => c.id);
  const defaultVisibility = defaultColumns.reduce((acc, c) => {
    acc[c.id] = c.visible !== false;
    return acc;
  }, {});
  const defaultWidths = defaultColumns.reduce((acc, c) => {
    acc[c.id] = c.defaultWidth ?? c.minWidth ?? 120;
    return acc;
  }, {});

  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.order !== "undefined") return parsed;
    } catch (_) {}
    return null;
  });

  const order = state?.order ?? defaultOrder;
  const visibility = state?.visibility ?? defaultVisibility;
  const widths = state?.widths ?? defaultWidths;

  const setOrder = useCallback(
    (nextOrder) => {
      setState((prev) => {
        const next = {
          order: typeof nextOrder === "function" ? nextOrder(prev?.order ?? defaultOrder) : nextOrder,
          visibility: prev?.visibility ?? defaultVisibility,
          widths: prev?.widths ?? defaultWidths,
        };
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch (_) {}
        return next;
      });
    },
    [key, defaultOrder, defaultVisibility, defaultWidths]
  );

  const setVisibility = useCallback(
    (id, visible) => {
      setState((prev) => {
        const next = {
          order: prev?.order ?? defaultOrder,
          visibility: { ...(prev?.visibility ?? defaultVisibility), [id]: visible },
          widths: prev?.widths ?? defaultWidths,
        };
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch (_) {}
        return next;
      });
    },
    [key, defaultOrder, defaultVisibility, defaultWidths]
  );

  const setWidth = useCallback(
    (id, width) => {
      setState((prev) => {
        const next = {
          order: prev?.order ?? defaultOrder,
          visibility: prev?.visibility ?? defaultVisibility,
          widths: { ...(prev?.widths ?? defaultWidths), [id]: Math.max(60, width) },
        };
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch (_) {}
        return next;
      });
    },
    [key, defaultOrder, defaultVisibility, defaultWidths]
  );

  const resetToDefaults = useCallback(() => {
    setState(null);
    try {
      localStorage.removeItem(key);
    } catch (_) {}
  }, [key]);

  const visibleColumnsInOrder = order.filter((id) => visibility[id] !== false);

  return {
    order,
    visibility,
    widths,
    visibleColumnsInOrder,
    setOrder,
    setVisibility,
    setWidth,
    resetToDefaults,
    defaultColumns,
  };
}

export default useListViewMode;
